import { useId } from 'react';
import './Pitch.css';
import { buildPitchAriaLabel } from './tactics2d/PitchSvg';

export interface PitchPositions {
  home: { x: number; y: number; role?: string }[];
  away: { x: number; y: number; role?: string }[];
  ball?: { x: number; y: number };
  arrow?: { from: { x: number; y: number }; to: { x: number; y: number }; via?: { x: number; y: number } };
}

interface PitchProps {
  caption?: string;
  width?: number;
  height?: number;
  className?: string;
  positions?: PitchPositions;
  /**
   * Optional team labels + formation codes. When provided we build the
   * same aria-label the interactive 2D board uses so screen readers
   * hear them identically. When omitted, falls back to `caption`.
   */
  homeLabel?: string;
  homeFormation?: string;
  awayLabel?: string;
  awayFormation?: string;
}

const DEFAULT_POSITIONS: PitchPositions = {
  home: [
    { x: 80, y: 60 },
    { x: 80, y: 140 },
    { x: 120, y: 100 },
    { x: 160, y: 60 },
    { x: 160, y: 140 },
  ],
  away: [
    { x: 220, y: 80 },
    { x: 220, y: 120 },
    { x: 280, y: 100 },
  ],
  ball: { x: 150, y: 100 },
  arrow: {
    from: { x: 150, y: 100 },
    via: { x: 190, y: 70 },
    to: { x: 230, y: 90 },
  },
};

/**
 * Mini animated pitch SVG. Deterministic — never AI-rendered pixels.
 * Pass `positions` to render an arbitrary drill layout. When omitted,
 * falls back to a 5v3 wide-overload rondo as the wireframe default.
 */
export function Pitch({
  caption = 'WIDE OVERLOAD 4v4',
  width = 360,
  height = 200,
  className = '',
  positions = DEFAULT_POSITIONS,
  homeLabel,
  homeFormation,
  awayLabel,
  awayFormation,
}: PitchProps) {
  const reactId = useId();
  const id = `pitch-${reactId.replace(/[^a-z0-9-]/gi, '')}`;
  const arrowPath = positions.arrow
    ? buildArrowPath(positions.arrow.from, positions.arrow.to, positions.arrow.via)
    : null;

  // Match the interactive 2D board's label format whenever team data
  // is provided; otherwise lean on the existing caption fallback so
  // existing drill-card uses (caption = drill name) keep working.
  const ariaLabel =
    homeLabel || homeFormation || awayLabel || awayFormation
      ? buildPitchAriaLabel({ homeLabel, homeFormation, awayLabel, awayFormation })
      : caption;

  return (
    <div className={`pitch ${className}`.trim()}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={ariaLabel}>
        <rect x="20" y="20" width={width - 40} height={height - 40} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <line x1={width / 2} y1="20" x2={width / 2} y2={height - 20} stroke="rgba(255,255,255,0.18)" />
        <circle cx={width / 2} cy={height / 2} r="22" fill="none" stroke="rgba(255,255,255,0.18)" />

        <rect x="20" y={height / 2 - 36} width="32" height="72" fill="none" stroke="rgba(255,255,255,0.10)" />
        <rect x={width - 52} y={height / 2 - 36} width="32" height="72" fill="none" stroke="rgba(255,255,255,0.10)" />

        {positions.home.map((p, i) => (
          <circle key={`h-${i}`} cx={p.x} cy={p.y} r="6" fill="var(--green)" />
        ))}
        {positions.away.map((p, i) => (
          <circle key={`a-${i}`} cx={p.x} cy={p.y} r="6" fill="var(--rose)" />
        ))}
        {positions.ball && <circle cx={positions.ball.x} cy={positions.ball.y} r="4" fill="#fff" />}

        {arrowPath && (
          <>
            <defs>
              <marker
                id={`${id}-ah`}
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path d="M0,0 L8,5 L0,10 z" fill="var(--green-bright)" />
              </marker>
            </defs>
            <path
              d={arrowPath}
              fill="none"
              stroke="var(--green-bright)"
              strokeWidth="2"
              strokeDasharray="5 4"
              markerEnd={`url(#${id}-ah)`}
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-18" dur="1.4s" repeatCount="indefinite" />
            </path>
          </>
        )}

        <text x="30" y="36" fontFamily="var(--font-mono)" fontSize="9" fill="var(--fg-3)" letterSpacing="2">
          {caption}
        </text>
      </svg>
    </div>
  );
}

function buildArrowPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
  via?: { x: number; y: number },
): string {
  if (via) return `M${from.x},${from.y} Q${via.x},${via.y} ${to.x},${to.y}`;
  return `M${from.x},${from.y} L${to.x},${to.y}`;
}
